import React, { useEffect, useMemo } from 'react';
import { ProfileEventTypes, ProfileUpComm } from './ProfileData';
import { UserDataKeys } from './UserDataKeys';
import { getTimezones } from '../../../api';
import { Option } from '../../magma-wrap/shared';
import { AsyncSelect } from '../../magma-wrap/AsyncSelect';

export const TEST_ID = {
  MAIN: 'timezone-select',
  TZ_SELECT_LOADING: 'loading-timezone'
};
const LABEL = 'Timezone';

export interface TimezoneSelectProps extends ProfileUpComm {
  value?: string;
  required?: boolean;
  name?: string;
  onFocus?: () => void;
  onChange?: (option: Option) => void;
  errorMessage?: string | null;
  __testValues?: Option[];
}

export const TimezoneSelect: React.FunctionComponent<TimezoneSelectProps> = (
  props: TimezoneSelectProps
) => {
  const { __testValues, upComm, required, value, onFocus, onChange, errorMessage } = props;

  const initLoader = useMemo(() => {
    return (async () => {
      return __testValues ?? (await getTimezones()) ?? [];
    })();
  }, [__testValues]);
  const initValue = useMemo(() => {
    return Promise.resolve(initLoader).then(v => v.find(w => w.item === value));
  }, [initLoader, value]);

  useEffect(() => {
    // This effect is meant to send a message telling the target
    // UpComm that this field is required.
    upComm?.sendMsg({
      type: ProfileEventTypes.dataRequired,
      data: {
        key: UserDataKeys.timezone,
        value: required,
        label: LABEL
      }
    });
  }, [required, upComm]);

  if (upComm?.ctx?.overrides?.hidden?.[UserDataKeys.timezone]) {
    upComm?.sendMsg({
      type: ProfileEventTypes.dataInit,
      data: {
        key: UserDataKeys.timezone,
        value: upComm.ctx.overrides.values[UserDataKeys.timezone]
      }
    });
    return <React.Fragment></React.Fragment>;
  }

  return (
    <div className={required ? 'required-input' : ''} style={{ marginBottom: 15 }}>
      <AsyncSelect
        labelText={LABEL}
        id="timezone-select"
        placeholder="Search by timezone name"
        filterOptions={(value, option) => {
          return option.label.toLowerCase().includes(value.toLowerCase());
        }}
        onInputFocus={onFocus}
        onSelectedItemChange={changes => {
          if (changes.selectedItem) {
            const { item } = changes.selectedItem;

            upComm &&
              upComm.sendMsg({
                type: ProfileEventTypes.dataChanged,
                data: {
                  key: UserDataKeys.timezone,
                  value: item
                }
              });

            onChange?.({ item } as Option);
          }
        }}
        initItems={initLoader}
        initValue={initValue}
        testId={TEST_ID.MAIN}
        errorMessage={errorMessage}
      />
    </div>
  );
};
